package com.arenaproplay.admin.pages.analytics

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.charts.svg.Donut
import com.arenaproplay.admin.ui.components.charts.svg.DonutSlice
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.numMedium
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.utils.countryFlag
import com.arenaproplay.admin.ui.utils.countryNameRu
import com.arenaproplay.admin.ui.utils.sBorderRadius
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlin.math.roundToInt

// ─────────────────────────────────────────────────────────────────────────────
// «Источники и гео» — kontaq PlatformSourcesView composition in Night Arena
// tokens. Block order:
//   [KPI chips] → [1] Обзор (ex-/dashboard: сессии + онлайн + выручка + график)
//   → [2] Страны | Города → [3] Источники | Кампании
//   → [4] Устройства | Платформы (donuts) → [Интересы] → [Инсайт] → [Сноски]
// Воронка реклама→PRO переехала на вкладку «Сессии» (kontaq pattern).
// ─────────────────────────────────────────────────────────────────────────────

// ── Source display helpers (Night Arena tokens) ──────────────────────────────

private val naSourceColors = mapOf(
    "meta"    to "#4D8CFF",   // Primary light
    "google"  to "#00E5A4",   // Mint
    "tiktok"  to "#E4CD40",   // Yellow
    "organic" to "#6F727A",   // Faint/meta
)

private val naSourceDisplayName = mapOf(
    "meta"    to "Meta Ads",
    "google"  to "Google Ads",
    "tiktok"  to "TikTok",
    "organic" to "Organic / не определён",
)

private val naSourceAttrType = mapOf(
    "meta"    to "utm",
    "google"  to "referrer",
    "tiktok"  to "utm",
    "organic" to null,
)

private val naDonutColors = listOf("#4D8CFF", "#00E5A4", "#E4CD40", "#8FB8FF", "#FF6B5B", "#0BAB64")

private fun deviceTypeLabelRu(raw: String): String = when (raw.lowercase()) {
    "mobile"  -> "Смартфон"
    "tablet"  -> "Планшет"
    "desktop" -> "Десктоп"
    "", "unknown" -> "Не определено"
    else      -> raw.replaceFirstChar { it.uppercase() }
}

private fun platformLabel(raw: String): String = when (raw.lowercase()) {
    "ios"     -> "iOS"
    "android" -> "Android"
    "web"     -> "Web"
    else      -> raw
}

// ─────────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun SourcesTabContent(
    summary: AcqSummaryDto?,
    sources: List<AcqSourceDto>?,
    geo: AcqGeoDto?,
    interests: List<AcqInterestDto>?,
    deviceShares: List<DeviceShareRow>?,
    behaviorKpis: BehaviorKpis?,
    subKpis: SubscriptionKpis?,
    onlineNow: OnlineNow?,
    activeUsersSeries: CategorizedTimeSeries?,
    eventsSeries: CategorizedTimeSeries?,
    baselineTs: String?,
    period: Period,
    summaryError: String?,
    sourcesError: String?,
    geoError: String?,
    interestsError: String?,
    devicesError: String?,
    kpisError: String?,
    chartError: String?,
) {
    // ── KPI chips (retained from Привлечение) ─────────────────────────────────
    NaAcqKpiRow(summary, summaryError)

    VSpace(16)

    // ── [1] Обзор — данные бывшего «Командного центра» (вместо карты мира) ────
    AnalyticsOverviewBlock(
        behaviorKpis = behaviorKpis,
        subKpis = subKpis,
        onlineNow = onlineNow,
        activeUsersSeries = activeUsersSeries,
        eventsSeries = eventsSeries,
        baselineTs = baselineTs,
        period = period,
        kpisError = kpisError,
        chartError = chartError,
    )

    VSpace(16)

    // ── [2] Страны | Города ───────────────────────────────────────────────────
    NaTwoCol(
        first = {
            GlassCard(radius = 16, padding = 20) {
                NaRankHeader("СТРАНЫ", "Посетители")
                when {
                    geoError != null -> NaSrcError(geoError)
                    geo == null -> NaSrcSkeleton(160)
                    geo.countries.isEmpty() -> NaSrcEmpty("Нет данных за период")
                    else -> {
                        val max = geo.countries.maxOf { it.users }.coerceAtLeast(1L)
                        Column(Modifier.fillMaxWidth().styleModifier { property("gap", "4px") }) {
                            geo.countries.forEach { c ->
                                NaRankRow(
                                    label    = countryNameRu(c.country),
                                    count    = c.users,
                                    fraction = c.users.toDouble() / max,
                                    flag     = countryFlag(c.country),
                                    extra    = "${(c.share * 100).roundToInt()}%",
                                )
                            }
                        }
                    }
                }
            }
        },
        second = {
            GlassCard(radius = 16, padding = 20) {
                NaRankHeader("ГОРОДА", "Посетители")
                when {
                    geoError != null -> NaSrcError(geoError)
                    geo == null -> NaSrcSkeleton(160)
                    geo.topCities.isEmpty() -> NaSrcEmpty("Нет данных за период")
                    else -> {
                        val max = geo.topCities.maxOf { it.users }.coerceAtLeast(1L)
                        Column(Modifier.fillMaxWidth().styleModifier { property("gap", "4px") }) {
                            geo.topCities.forEach { city ->
                                NaRankRow(
                                    label    = city.city,
                                    count    = city.users,
                                    fraction = city.users.toDouble() / max,
                                )
                            }
                        }
                    }
                }
            }
        },
    )

    VSpace(16)

    // ── [3] Источники | Кампании ──────────────────────────────────────────────
    NaTwoCol(
        first = {
            GlassCard(radius = 16, padding = 20) {
                NaRankHeader("ИСТОЧНИКИ", "Юзеры")
                when {
                    sourcesError != null -> NaSrcError(sourcesError)
                    sources == null -> NaSrcSkeleton(160)
                    // kontaq: пустые источники = органика / прямые заходы
                    sources.isEmpty() || sources.all { it.users == 0L } ->
                        NaSrcEmpty("Прямые заходы")
                    else -> {
                        val max = sources.maxOf { it.users }.coerceAtLeast(1L)
                        Column(Modifier.fillMaxWidth().styleModifier { property("gap", "4px") }) {
                            sources.forEach { src ->
                                val key = src.source.lowercase()
                                NaRankRow(
                                    label     = naSourceDisplayName[key] ?: src.source,
                                    count     = src.users,
                                    fraction  = src.users.toDouble() / max,
                                    dotColor  = naSourceColors[key] ?: "#6F727A",
                                    monoBadge = naSourceAttrType[key] ?: "organic",
                                    extra     = "актив ${formatNumber(src.active24h)} · " +
                                        "PRO ${formatNumber(src.pro)} · ${(src.convRate * 100).format(1)}%",
                                )
                            }
                        }
                    }
                }
            }
        },
        second = {
            GlassCard(radius = 16, padding = 20) {
                NaRankHeader("КАМПАНИИ", "Юзеры")
                when {
                    sourcesError != null -> NaSrcError(sourcesError)
                    sources == null -> NaSrcSkeleton(160)
                    else -> {
                        val campaigns = sources
                            .flatMap { src -> src.campaigns.map { Triple(src.source, it.campaign, it) } }
                            .sortedByDescending { it.third.users }
                        if (campaigns.isEmpty()) {
                            NaSrcEmpty("Пока нет данных")
                        } else {
                            val max = campaigns.maxOf { it.third.users }.coerceAtLeast(1L)
                            Column(Modifier.fillMaxWidth().styleModifier { property("gap", "4px") }) {
                                campaigns.forEach { (sourceKey, campaign, c) ->
                                    val srcName = naSourceDisplayName[sourceKey.lowercase()] ?: sourceKey
                                    NaRankRow(
                                        label    = if (campaign == "(none)") "— без кампании · $srcName"
                                                   else "$campaign · $srcName",
                                        count    = c.users,
                                        fraction = c.users.toDouble() / max,
                                        dotColor = naSourceColors[sourceKey.lowercase()] ?: "#6F727A",
                                        extra    = "PRO ${formatNumber(c.pro)}",
                                    )
                                }
                            }
                        }
                    }
                }
            }
        },
    )

    VSpace(16)

    // ── [4] Устройства | Платформы (donuts) ───────────────────────────────────
    NaTwoCol(
        first = {
            NaDonutCard(
                title   = "УСТРОЙСТВА",
                entries = deviceShares
                    ?.groupBy { it.deviceType.lowercase() }
                    ?.map { (k, v) -> deviceTypeLabelRu(k) to v.sumOf { it.users } }
                    ?.sortedByDescending { it.second },
                error   = devicesError,
            )
        },
        second = {
            NaDonutCard(
                title   = "ПЛАТФОРМЫ",
                entries = deviceShares
                    ?.groupBy { it.platform.lowercase() }
                    ?.map { (k, v) -> platformLabel(k) to v.sumOf { it.users } }
                    ?.sortedByDescending { it.second },
                error   = devicesError,
            )
        },
    )

    VSpace(16)

    // ── Интересы новых юзеров (retained from Привлечение) ─────────────────────
    NaInterestsCard(interests, interestsError)

    // ── Data-driven insight callout ───────────────────────────────────────────
    NaAcqInsightCallout(sources = sources, interests = interests)

    // ── Honest footnotes ──────────────────────────────────────────────────────
    VSpace(12)
    Column(
        Modifier.fillMaxWidth()
            .styleModifier {
                property("padding", "0 4px")
                property("gap", "3px")
            }
    ) {
        SpanText(
            "⚠ iOS-установки из стора не атрибутируются (ограничение Apple). " +
                "UTM ловится при регистрации в том же браузере, что и клик по рекламе; " +
                "Android Install Referrer заработает после обновления мобильного приложения.",
            Modifier.styleModifier {
                property("font-size", "11px")
                property("color", "rgba(240,244,252,.38)")
                property("line-height", "1.5")
            }
        )
        SpanText(
            "ℹ Полные колонки по источникам (юзеры / актив 24ч / PRO / conv) доступны в CSV-экспорте. " +
                "Воронка реклама → PRO — на вкладке «Сессии».",
            Modifier.styleModifier {
                property("font-size", "11px")
                property("color", "rgba(240,244,252,.38)")
                property("line-height", "1.5")
            }
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// KPI Row (moved from Analytics.kt / Привлечение — capability retained)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun NaAcqKpiRow(summary: AcqSummaryDto?, summaryError: String?) {
    Row(
        Modifier.fillMaxWidth()
            .styleModifier {
                property("gap", "12px")
                property("flex-wrap", "wrap")
            }
    ) {
        // Chip 1: Новые юзеры
        GlassCard(modifier = Modifier.styleModifier { property("flex", "1 1 160px") }, padding = 16) {
            SpanText("Новые юзеры", Modifier.eyebrow().color(AppColors.Faint))
            Box(Modifier.height(5.px))
            when {
                summaryError != null -> NaChipError()
                summary == null -> NaChipSkeleton()
                else -> SpanText(formatNumber(summary.newUsers), Modifier.numMedium().color(AppColors.Ink))
            }
            if (summary != null) {
                Box(Modifier.height(3.px))
                SpanText("за период", Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.45)")
                })
            }
        }

        // Chip 2: Из рекламы
        GlassCard(modifier = Modifier.styleModifier { property("flex", "1 1 160px") }, padding = 16) {
            SpanText("Из рекламы", Modifier.eyebrow().color(AppColors.Faint))
            Box(Modifier.height(5.px))
            when {
                summaryError != null -> NaChipError()
                summary == null -> NaChipSkeleton()
                else -> {
                    val pct = if (summary.newUsers > 0)
                        (summary.attributed.toDouble() / summary.newUsers * 100).roundToInt()
                    else 0
                    Row(Modifier.styleModifier { property("align-items", "baseline"); property("gap", "6px") }) {
                        SpanText("$pct%", Modifier.numMedium().color(AppColors.Ink))
                        SpanText(
                            "${formatNumber(summary.attributed)} юзеров",
                            Modifier.bodySRegular().styleModifier { property("color", "rgba(240,244,252,.45)") }
                        )
                    }
                }
            }
            if (summary != null) {
                Box(Modifier.height(3.px))
                SpanText("UTM / referrer", Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.45)")
                })
            }
        }

        // Chip 3: Актив 24ч
        GlassCard(modifier = Modifier.styleModifier { property("flex", "1 1 160px") }, padding = 16) {
            SpanText("Актив первые 24ч", Modifier.eyebrow().color(AppColors.Faint))
            Box(Modifier.height(5.px))
            when {
                summaryError != null -> NaChipError()
                summary == null -> NaChipSkeleton()
                else -> SpanText(
                    "${(summary.active24hRate * 100).format(1)}%",
                    Modifier.numMedium().color(AppColors.Ink)
                )
            }
            if (summary != null) {
                Box(Modifier.height(3.px))
                SpanText("действие в первые 24ч", Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.45)")
                })
            }
        }

        // Chip 4: PRO конверсия
        GlassCard(modifier = Modifier.styleModifier { property("flex", "1 1 160px") }, padding = 16) {
            SpanText("→ PRO конверсия", Modifier.eyebrow().color(AppColors.Faint))
            Box(Modifier.height(5.px))
            when {
                summaryError != null -> NaChipError()
                summary == null -> NaChipSkeleton()
                else -> {
                    val convPct = summary.proConversionRate * 100.0
                    Row(Modifier.styleModifier { property("align-items", "baseline"); property("gap", "6px") }) {
                        SpanText(
                            "${convPct.format(1)}%",
                            Modifier.numMedium().color(AppColors.Ink)
                        )
                        SpanText(
                            "${formatNumber(summary.proConversions)} PRO",
                            Modifier.bodySRegular()
                                .styleModifier { property("color", "#E4CD40"); property("font-size", "11px") }
                        )
                    }
                }
            }
            if (summary != null) {
                Box(Modifier.height(3.px))
                SpanText("новых за период", Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.45)")
                })
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Interests card + insight callout (moved from Analytics.kt — capability retained)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun NaInterestsCard(interests: List<AcqInterestDto>?, error: String?) {
    GlassCard(radius = 16, padding = 20) {
        Row(
            Modifier.fillMaxWidth()
                .styleModifier { property("align-items", "center"); property("gap", "8px") }
        ) {
            SpanText(
                "ЧЕМ ИНТЕРЕСУЮТСЯ НОВЫЕ ЮЗЕРЫ",
                Modifier.eyebrow().color(AppColors.Faint)
                    .styleModifier { property("flex", "1") }
            )
            SpanText(
                "первые 7 дней · доля просмотров",
                Modifier.styleModifier {
                    property("font-size", "11px")
                    property("color", "rgba(240,244,252,.35)")
                    property("text-align", "right")
                    property("line-height", "1.4")
                }
            )
        }
        VSpace(12)

        when {
            error != null -> NaSrcError(error)
            interests == null -> NaSrcSkeleton(120)
            interests.isEmpty() -> NaSrcEmpty("Нет данных за период")
            else -> {
                Div(attrs = Modifier.fillMaxWidth()
                        .styleModifier {
                            property("display", "grid")
                            property("grid-template-columns", "minmax(0,1fr) minmax(0,1fr)")
                            property("gap", "8px 20px")
                        }.toAttrs()
                ) {
                    interests.take(8).forEach { item ->
                        val pct = (item.share * 100).format(0)
                        val widthPct = (item.share * 100).coerceIn(1.0, 100.0)
                        val barColor = when {
                            item.share >= 0.5 -> "#0061FF"
                            item.share >= 0.3 -> "#00E5A4"
                            else -> "#4D8CFF"
                        }
                        Column(Modifier.fillMaxWidth()) {
                            Row(
                                Modifier.fillMaxWidth()
                                    .styleModifier { property("margin-bottom", "4px"); property("align-items", "center") }
                            ) {
                                SpanText(
                                    item.screen,
                                    Modifier.styleModifier {
                                        property("flex", "1")
                                        property("font-size", "11.5px")
                                        property("color", "#F0F4FC")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                    }
                                )
                                SpanText(
                                    "$pct%",
                                    Modifier.styleModifier {
                                        property("font-family", "'Unbounded', sans-serif")
                                        property("font-variant-numeric", "tabular-nums")
                                        property("font-size", "11px")
                                        property("font-weight", "600")
                                        property("color", "#F0F4FC")
                                    }
                                )
                            }
                            Box(
                                Modifier.fillMaxWidth().height(5.px).sBorderRadius(3)
                                    .styleModifier {
                                        property("background", "rgba(255,255,255,.06)")
                                        property("overflow", "hidden")
                                    }
                            ) {
                                Box(
                                    Modifier.height(5.px).sBorderRadius(3)
                                        .styleModifier {
                                            property("width", "$widthPct%")
                                            property("background", barColor)
                                        }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NaAcqInsightCallout(
    sources: List<AcqSourceDto>?,
    interests: List<AcqInterestDto>?,
) {
    if (sources == null || interests == null) return

    val topPaidSource = sources
        .filter { it.source.lowercase() != "organic" && it.users > 0 }
        .maxByOrNull { it.users }
        ?: return

    val topInterest = interests.firstOrNull { it.share > 0 } ?: return

    val sourceName = naSourceDisplayName[topPaidSource.source.lowercase()] ?: topPaidSource.source
    val sharePct = (topInterest.share * 100).format(0)
    val insightText =
        "Инсайт: главный платный источник — $sourceName (${formatNumber(topPaidSource.users)} юзеров), " +
            "самый популярный раздел у новых юзеров — ${topInterest.screen} ($sharePct%)."

    Box(Modifier.height(10.px))
    Box(
        Modifier.fillMaxWidth().sBorderRadius(12)
            .styleModifier {
                property("background", "rgba(228,205,64,.10)")
                property("border", "1px solid rgba(228,205,64,.25)")
                property("padding", "10px 14px")
                property("display", "flex")
                property("align-items", "flex-start")
                property("gap", "8px")
            }
    ) {
        SpanText(
            "⚡",
            Modifier.styleModifier {
                property("font-size", "13px")
                property("flex-shrink", "0")
                property("line-height", "1.6")
            }
        )
        SpanText(
            insightText,
            Modifier.styleModifier {
                property("font-size", "12px")
                property("font-weight", "600")
                property("color", "#E4CD40")
                property("line-height", "1.6")
            }
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Building blocks
// ─────────────────────────────────────────────────────────────────────────────

/** Two-column responsive grid — kontaq QpTwoColumn analog on our grid pattern. */
@Composable
private fun NaTwoCol(first: @Composable () -> Unit, second: @Composable () -> Unit) {
    // Bug-class 1 fix: minmax(0,1fr) prevents unwrappable content from blowing tracks
    Div(attrs = Modifier.fillMaxWidth().styleModifier {
            property("display", "grid")
            property("grid-template-columns", "minmax(0,1fr) minmax(0,1fr)")
            property("gap", "16px")
            property("align-items", "start")
        }.toAttrs()
    ) {
        first()
        second()
    }
}

/** Card header: eyebrow title left + dim column hint right (kontaq trailingHint). */
@Composable
private fun NaRankHeader(title: String, trailingHint: String?) {
    Row(
        Modifier.fillMaxWidth()
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "space-between")
                property("margin-bottom", "12px")
            }
    ) {
        SpanText(title, Modifier.eyebrow().color(AppColors.Faint))
        if (trailingHint != null) {
            SpanText(
                trailingHint,
                Modifier.styleModifier {
                    property("font-size", "10px")
                    property("color", "rgba(240,244,252,.45)")
                }
            )
        }
    }
}

/**
 * Ranked row — kontaq .qp-rank anatomy: proportional alpha-fill bar behind a
 * flex row: [flag|dot] label … count [extra]. Fill = NA Primary at .16.
 */
@Composable
private fun NaRankRow(
    label: String,
    count: Long,
    fraction: Double,
    flag: String? = null,
    dotColor: String? = null,
    monoBadge: String? = null,
    extra: String? = null,
) {
    Box(
        Modifier.fillMaxWidth().sBorderRadius(9)
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")
            }
    ) {
        // Fill bar behind content (min 2% so tiny values are visible)
        Box(
            Modifier.styleModifier {
                property("position", "absolute")
                property("left", "0")
                property("top", "0")
                property("bottom", "0")
                property("width", "${(fraction * 100).coerceAtLeast(2.0)}%")
                property("background", "rgba(0,97,255,.16)")
                property("pointer-events", "none")
            }
        )
        Row(
            Modifier.fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("padding", "8px 11px")
                    property("align-items", "center")
                    property("justify-content", "space-between")
                    property("gap", "8px")
                }
        ) {
            // Left: flag/dot + label + optional mono badge
            Row(
                Modifier.styleModifier {
                    property("align-items", "center")
                    property("gap", "7px")
                    property("min-width", "0")
                    property("flex", "1")
                }
            ) {
                if (flag != null) {
                    SpanText(flag, Modifier.styleModifier { property("font-size", "14px"); property("flex-shrink", "0") })
                }
                if (dotColor != null) {
                    Box(
                        Modifier.width(8.px).height(8.px).sBorderRadius(2)
                            .styleModifier { property("background", dotColor); property("flex-shrink", "0") }
                    )
                }
                SpanText(
                    label,
                    Modifier.styleModifier {
                        property("font-size", "12px")
                        property("color", "#F0F4FC")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
                )
                if (monoBadge != null) {
                    Box(
                        Modifier.styleModifier {
                            property("background", "rgba(255,255,255,.07)")
                            property("border-radius", "4px")
                            property("padding", "1px 5px")
                            property("flex-shrink", "0")
                        }
                    ) {
                        SpanText(
                            monoBadge,
                            Modifier.styleModifier {
                                property("font-family", "'JetBrains Mono', monospace")
                                property("font-size", "10px")
                                property("color", "rgba(240,244,252,.50)")
                                property("letter-spacing", ".04em")
                            }
                        )
                    }
                }
            }
            // Right: count + optional extra
            Row(
                Modifier.styleModifier {
                    property("align-items", "baseline")
                    property("gap", "6px")
                    property("flex-shrink", "0")
                }
            ) {
                SpanText(
                    formatNumber(count),
                    Modifier.styleModifier {
                        property("font-family", "'Unbounded', sans-serif")
                        property("font-variant-numeric", "tabular-nums")
                        property("font-size", "12px")
                        property("font-weight", "700")
                        property("color", "#F0F4FC")
                        property("white-space", "nowrap")
                    }
                )
                if (extra != null) {
                    SpanText(
                        extra,
                        Modifier.styleModifier {
                            property("font-size", "11px")
                            property("color", "rgba(240,244,252,.45)")
                            property("white-space", "nowrap")
                        }
                    )
                }
            }
        }
    }
}

/**
 * Donut card — kontaq QpDonut composition: donut left with the centered total
 * (Unbounded), legend right with square swatches + bold values.
 */
@Composable
private fun NaDonutCard(title: String, entries: List<Pair<String, Long>>?, error: String?) {
    GlassCard(radius = 16, padding = 20) {
        SpanText(title, Modifier.eyebrow().color(AppColors.Faint))
        VSpace(14)
        when {
            error != null -> NaSrcError(error)
            entries == null -> NaSrcSkeleton(132)
            entries.isEmpty() || entries.sumOf { it.second } == 0L -> NaSrcEmpty("Нет данных за период")
            else -> {
                val total = entries.sumOf { it.second }
                val slices = entries.mapIndexed { i, (name, v) ->
                    DonutSlice(name = name, value = v.toDouble(), colorHex = naDonutColors[i % naDonutColors.size])
                }
                Row(
                    Modifier.fillMaxWidth()
                        .styleModifier { property("align-items", "center"); property("gap", "16px") }
                ) {
                    // Donut + centre total overlay
                    Box(
                        Modifier.width(132.px).height(132.px)
                            .styleModifier { property("position", "relative"); property("flex-shrink", "0") },
                        contentAlignment = Alignment.Center,
                    ) {
                        Donut(slices = slices, sizePx = 132, strokeWidth = 12)
                        SpanText(
                            formatNumber(total),
                            Modifier.styleModifier {
                                property("position", "absolute")
                                property("left", "50%")
                                property("top", "50%")
                                property("transform", "translate(-50%,-50%)")
                                property("font-family", "'Unbounded', sans-serif")
                                property("font-size", "18px")
                                property("font-weight", "700")
                                property("color", "#F0F4FC")
                            }
                        )
                    }
                    // Legend
                    Column(
                        Modifier.styleModifier {
                            property("gap", "7px")
                            property("flex", "1")
                            property("min-width", "0")
                        }
                    ) {
                        entries.forEachIndexed { i, (name, v) ->
                            Row(
                                Modifier.fillMaxWidth()
                                    .styleModifier {
                                        property("align-items", "center")
                                        property("justify-content", "space-between")
                                        property("gap", "8px")
                                    }
                            ) {
                                Row(
                                    Modifier.styleModifier {
                                        property("align-items", "center")
                                        property("gap", "8px")
                                        property("min-width", "0")
                                    }
                                ) {
                                    Box(
                                        Modifier.width(9.px).height(9.px).sBorderRadius(3)
                                            .styleModifier {
                                                property("background", naDonutColors[i % naDonutColors.size])
                                                property("flex-shrink", "0")
                                            }
                                    )
                                    SpanText(
                                        name,
                                        Modifier.styleModifier {
                                            property("font-size", "12px")
                                            property("color", "rgba(240,244,252,.82)")
                                            property("overflow", "hidden")
                                            property("text-overflow", "ellipsis")
                                            property("white-space", "nowrap")
                                        }
                                    )
                                }
                                SpanText(
                                    formatNumber(v),
                                    Modifier.styleModifier {
                                        property("font-size", "12px")
                                        property("font-weight", "700")
                                        property("color", "#F0F4FC")
                                        property("white-space", "nowrap")
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

// ── State helpers ─────────────────────────────────────────────────────────────

@Composable
private fun NaSrcSkeleton(height: Int) {
    Box(
        Modifier.fillMaxWidth().height(height.px).sBorderRadius(10)
            .styleModifier {
                property("background", "rgba(255,255,255,.04)")
                property("animation", "admin-skeleton 1.6s ease-in-out infinite")
            }
    )
}

@Composable
private fun NaSrcError(msg: String) {
    SpanText(
        "⚠ $msg",
        Modifier.bodySRegular().styleModifier { property("color", "#FF6B5B") }
    )
}

@Composable
private fun NaSrcEmpty(text: String) {
    SpanText(text, Modifier.bodySRegular().color(AppColors.Faint))
}

@Composable
private fun NaChipSkeleton() {
    Box(
        Modifier.fillMaxWidth().height(24.px)
            .styleModifier {
                property("background", "rgba(255,255,255,.04)")
                property("border-radius", "6px")
            }
    )
}

@Composable
private fun NaChipError() {
    SpanText(
        "Ошибка",
        Modifier.bodySRegular().styleModifier { property("color", "#FF6B5B") }
    )
}
